import { defineStore } from 'pinia';
import { ref, readonly, computed } from 'vue';
import { apiGetCurrentAccount } from '@/api/account';
import { useAuthUserStore } from '@/store/use-auth-user-store';
import { OrganizationDto } from '@/api/organizations';
import { Nullable } from '@/types/common';

export const useCurrentOrganizationStore = defineStore(
  'currentOrganization',
  () => {
    const currentOrganization = ref<Nullable<OrganizationDto>>(null);

    // TRANSITIONAL (RBAC migration): when an org is cut over to RBAC content
    // access, direct hub assignment is managed via roles, not by admins. Used
    // to hide legacy direct hub-assignment UI. Remove once all orgs migrated.
    const isRbacContentAccess = computed<boolean>(
      () =>
        // NOTE: `rbacContentAccessEnabled` is not yet present in the generated
        // api-types; access it via a localized cast until types are regenerated.
        (
          currentOrganization.value?.organizationSettings as
            | { rbacContentAccessEnabled?: boolean }
            | null
            | undefined
        )?.rbacContentAccessEnabled === true,
    );

    const isRbacRoles = computed<boolean>(
      () =>
        currentOrganization.value?.organizationSettings?.rbacRolesEnabled ===
        true,
    );

    const setCurrentOrganization = (data: OrganizationDto | null): void => {
      currentOrganization.value = data;
    };

    const clearCurrentOrganization = (): void => {
      currentOrganization.value = null;
    };

    const loadCurrentOrganization = async (organizationSlug: string) => {
      if (
        !currentOrganization.value ||
        currentOrganization.value.slug !== organizationSlug
      ) {
        const authUserStore = useAuthUserStore();
        const { setAuthAccount } = authUserStore;

        const account = await apiGetCurrentAccount(organizationSlug);
        setAuthAccount(account);

        setCurrentOrganization(account.organization);
      }
    };

    return {
      currentOrganization: readonly(currentOrganization),
      isRbacContentAccess,
      isRbacRoles,
      setCurrentOrganization,
      clearCurrentOrganization,
      loadCurrentOrganization,
    };
  },
);
